import { AdUnit } from '@pubstack/common/src/adunit'
import { BidParamsMapping, CreateBidParamsMapping, generateMappings, MappingDimensionsConfig } from '@pubstack/common/src/bid-params-mapping'
import { BidderSeat, productOfBidderSeatType } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog, isMarketplaceAlias } from '@pubstack/common/src/bidderCatalog'
import { DEVICE_TYPES } from '@pubstack/common/src/device-type'
import { MEDIA_TYPES } from '@pubstack/common/src/media-types'
import { StacksBySite } from '@pubstack/common/src/stack'
import { partition } from 'lodash-es'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useBulkUpsertBidParamsMapping } from '~/api/bid-params-mappings.api'
import { useCatalog } from '~/api/catalog.api'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Input } from '~/components/Input'
import { Qualifier } from '~/components/Qualifier'
import { Select } from '~/components/Select'
import { Skeleton } from '~/components/Skeleton'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import { bidParamSpecsOfSeat } from '~/modules/pbs/integrations/bidder-seats/bidder-seat/mappingInventory'
import { MappingsSpreadsheet } from '~/modules/pbs/integrations/bidder-seats/bidder-seat/MappingsSpreadsheet'
import { useConfirmNavigation } from '~/utils/useConfirmNavigation'
import { generateStackNameList } from '../../rules/PureUsedStackTooltip'
import { AdmSitePageContext } from '~/modules/pbs/sites/site/useSitePageContext'
import { useMediaMappingSeats } from './useMediaMappingSeats'

const titleBlocClassName = 'inline-flex w-1/4 items-center gap-2 text-base leading-[1.625rem]'

const skeletonTitle = (
  <div className="inline-flex w-full items-center">
    {[0, 1, 2].map((i) => (
      <span key={i} className={titleBlocClassName}>
        <Skeleton width="15ch" />
      </span>
    ))}
  </div>
)

type AdUnitMappings = AdUnit & {
  usedNbOfDesktopBidders: number
  usedNbOfMobileBidders: number
  totalNbOfDesktopBidders: number
  totalNbOfMobileBidders: number
  mappedStacks: StacksBySite[string]
  mappings: Record<
    string,
    {
      bidParamsMappings: (BidParamsMapping | CreateBidParamsMapping)[]
      mappingDimensionsConfig: MappingDimensionsConfig
    }
  >
}

export const SiteMappingAdunitView: FunctionComponent = () => {
  // Hooks
  const { adUnits, stacks, site } = useOutletContext<AdmSitePageContext>()
  const navigate = useNavigate()
  const user = useAuthenticatedUser()

  // API Hooks
  const { data: catalogBidders = [], isLoading: isLoadingCatalogBidders } = useCatalog('adm')
  const { bidderSeats, existingMappings, refetchMappings, isLoading: isLoadingSeats } = useMediaMappingSeats(site.id)

  const adUnitsMappings: AdUnitMappings[] = useMemo(
    () =>
      adUnits.map((adUnit) => {
        const existingMappingsAdUnit = existingMappings.filter((mapping) => Object.keys(mapping.bidParams).length && mapping.dimensions.adUnitId === adUnit.id)
        const usedNbOfDesktopBidders = new Set(existingMappingsAdUnit.filter((mapping) => !mapping.dimensions.device || mapping.dimensions.device === 'desktop').map((mapping) => mapping.bidderSeatId))
          .size
        const usedNbOfMobileBidders = new Set(existingMappingsAdUnit.filter((mapping) => !mapping.dimensions.device || mapping.dimensions.device === 'mobile').map((mapping) => mapping.bidderSeatId))
          .size
        const mappedStacks = stacks
          .filter((stack) =>
            [
              ...stack.configuration.desktopAdUnitsIds,
              ...stack.configuration.mobileAdUnitsIds,
              // Roadblock ad units live in separate arrays (mutually exclusive with the regular ones),
              // so an ad unit used only as a roadblock is still "used" in the stack.
              ...(stack.configuration.desktopRoadblockAdUnitsIds ?? []),
              ...(stack.configuration.mobileRoadblockAdUnitsIds ?? []),
            ].some((id) => id === adUnit.id)
          )
          .map((stack) => ({ enabled: stack.status !== 'archived' && stack.status !== 'archived_group', stackName: stack.name, version: stack.version?.toString() ?? 'draft', type: 'adm' as const }))
        return {
          ...adUnit,
          usedNbOfDesktopBidders,
          usedNbOfMobileBidders,
          totalNbOfDesktopBidders: bidderSeats.length,
          totalNbOfMobileBidders: bidderSeats.length,
          mappedStacks,
          mappings: bidderSeats.reduce(
            (acc, bidderSeat) => {
              const hasMediaTypes = bidderSeat.mappingDimensions.includes('mediaType')
              const hasDevices = bidderSeat.mappingDimensions.includes('device')
              const catalogBidder = catalogBidders.find((c) => c.id === bidderSeat.bidderCode)
              const mappingDimensionsConfig: MappingDimensionsConfig = {
                siteId: {
                  values: [site.id],
                  labels: { [site.id]: site.name || '' },
                },
                adUnitId: {
                  values: [adUnit.id],
                  labels: { [adUnit.id]: adUnit.name },
                },
                ...(hasMediaTypes
                  ? {
                      mediaType: {
                        values: (catalogBidder?.supportedMediaTypes || Array.from(MEDIA_TYPES)).map((e) => e as string),
                        labels: Object.fromEntries((catalogBidder?.supportedMediaTypes || Array.from(MEDIA_TYPES)).map((e) => [e, e])),
                      },
                    }
                  : {}),
                ...(hasDevices
                  ? {
                      device: {
                        values: Array.from(DEVICE_TYPES),
                        labels: Object.fromEntries(Array.from(DEVICE_TYPES).map((e) => [e, e])),
                      },
                    }
                  : {}),
              }
              const mappings = generateMappings({
                existingMappings: existingMappingsAdUnit.filter((s) => s.bidderSeatId === bidderSeat.id),
                mappingDimensionsConfig,
                siteAdUnitCouplesToExclude: new Set(),
                bidderSeatId: bidderSeat.id,
                adUnits: [adUnit],
              })
              acc[bidderSeat.id] = {
                bidParamsMappings: mappings,
                mappingDimensionsConfig,
              }
              return acc
            },
            {} as AdUnitMappings['mappings']
          ),
        }
      }),
    [adUnits, stacks, site, catalogBidders, bidderSeats, existingMappings]
  )

  const {
    resetField,
    control,
    formState: { isDirty },
  } = useForm<{ search: string; usedOnThisSite: boolean }>({
    defaultValues: {
      search: '',
      usedOnThisSite: true,
    },
  })
  const { search, usedOnThisSite } = useWatch({ control })

  // Variables
  const searchedAdunits = adUnitsMappings.filter((a) => !search || a.name.toLowerCase().includes(search.toLowerCase()))
  const filteredAdunits = usedOnThisSite ? searchedAdunits.filter((a) => a.mappedStacks.length > 0) : searchedAdunits
  const pipeSeparator = `\xa0 | \xa0`

  const onCreateNewBidder = () => navigate('/monetization/inventory/catalog')

  return (
    <div className="grid gap-2.5">
      <form className="mb-3 flex gap-3.5">
        <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search ad unit'} control={control} />
        <Select
          control={control}
          name={'usedOnThisSite'}
          options={[
            { label: 'Used on this site', value: true, trackingLabel: 'adm-sites-stacks-mapping-ad-units-filter-active-apply' },
            { label: 'All ad units', value: false, trackingLabel: 'adm-sites-stacks-mapping-ad-units-no-filter-apply' },
          ]}
          label={'Ad units'}
        />
        {isDirty && (
          <Tooltip title={'Restore'}>
            <Button
              iconName={'close'}
              variant={'tertiary'}
              onClick={() => {
                resetField('search')
                resetField('usedOnThisSite')
              }}
              trackingLabel={'adm-sites-stacks-ad-units-mapping-filter-restore'}
            />
          </Tooltip>
        )}
      </form>
      {isLoadingCatalogBidders || isLoadingSeats ? (
        <>
          {[0, 1, 2].map((e) => (
            <CollapsiblePanel key={e} hasBorderWhenOpened isCollapsible={{ trackingLabel: 'adm-sites-stacks-ad-units-mapping-collapse-apply' }} title={skeletonTitle} />
          ))}
        </>
      ) : filteredAdunits.length > 0 ? (
        filteredAdunits.map((adUnit) => {
          return (
            <CollapsiblePanel
              key={adUnit.name}
              hasBorderWhenOpened
              isCollapsible={{ trackingLabel: 'adm-sites-stacks-ad-units-mapping-collapse-apply' }}
              title={
                <div className="inline-flex w-full items-center">
                  <div className="flex w-1/4 flex-col text-base leading-[1.625rem]">
                    <span>{adUnit.name}</span>
                  </div>
                  <span className={titleBlocClassName}>
                    <Qualifier iconName={'desktop'} tooltipText={'Desktop'} color={Colors.Petrol} active={true} enabled={true} />
                    {adUnit.usedNbOfDesktopBidders === 0 || adUnit.usedNbOfDesktopBidders !== adUnit.totalNbOfDesktopBidders ? (
                      <span>
                        {adUnit.usedNbOfDesktopBidders} bidder{adUnit.usedNbOfDesktopBidders > 1 && 's'}/alias{adUnit.usedNbOfDesktopBidders > 1 && 'es'}
                      </span>
                    ) : (
                      <span>All active bidders/aliases ({adUnit.totalNbOfDesktopBidders})</span>
                    )}
                  </span>
                  <span className={titleBlocClassName}>
                    <Qualifier iconName={'mobile'} tooltipText={'Mobile'} color={Colors.Pool} active={true} enabled={true} />
                    {adUnit.usedNbOfMobileBidders === 0 || adUnit.usedNbOfMobileBidders !== adUnit.totalNbOfMobileBidders ? (
                      <span>
                        {adUnit.usedNbOfMobileBidders} bidder{adUnit.usedNbOfMobileBidders > 1 && 's'}/alias{adUnit.usedNbOfMobileBidders > 1 && 'es'}
                      </span>
                    ) : (
                      <span>All active bidders/aliases ({adUnit.totalNbOfMobileBidders})</span>
                    )}
                  </span>
                  {adUnit.mappedStacks.length > 0 ? (
                    <Tooltip title={generateStackNameList(adUnit.mappedStacks).reduce((acc, stack) => [acc, pipeSeparator, stack])} positions={['left', 'bottom', 'top', 'right']}>
                      {adUnit.mappedStacks.length !== stacks.length ? (
                        <span>
                          {adUnit.mappedStacks.length} stack{adUnit.mappedStacks.length > 1 && 's'}
                        </span>
                      ) : (
                        <span>All stacks ({stacks.length})</span>
                      )}
                    </Tooltip>
                  ) : (
                    <span>0 stack</span>
                  )}
                </div>
              }
            >
              <AdUnitPanel adUnit={adUnit} bidderSeats={bidderSeats} catalogBidders={catalogBidders} isAdmin={user.isAdmin} refetchSavedMappings={refetchMappings} />
            </CollapsiblePanel>
          )
        })
      ) : (
        <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'It seems that no bidder mapping exist on your site.'} message={'Add bidder mapping to your inventory.'}>
          <Button iconName={'add_bidder'} variant={'primary'} onClick={onCreateNewBidder} trackingLabel={'adm-sites-stacks-ad-units-mapping-all-integration-navigate'}>
            {/*ASK AN EXPERT SLE 2024-07-22: When no ad unit match a filter we propose to add a bidder ! */}
            Add new bidder
          </Button>
        </IllustrationMessage>
      )}
    </div>
  )
}

type AdUnitPanelProps = {
  bidderSeats: BidderSeat[]
  catalogBidders: BidderCatalog[]
  adUnit: AdUnitMappings
  isAdmin: boolean
  refetchSavedMappings: () => Promise<unknown>
}

const AdUnitPanel: FunctionComponent<AdUnitPanelProps> = ({ bidderSeats, catalogBidders, adUnit, isAdmin, refetchSavedMappings }) => {
  // State
  const [hasErroredMappings, setHasErroredMappings] = useState<boolean>(false)
  const [draftMappings, setDraftMappings] = useState<Record<string, (BidParamsMapping | CreateBidParamsMapping)[]>>({})

  // Hooks
  const { canEdit, disabledReason } = useScopePermissions()
  const toast = useToast()
  const { control } = useForm({
    defaultValues: {
      bidderSearch: '',
    },
  })
  const bidderSearch = useWatch({ control, name: 'bidderSearch' })
  const filteredSeats = useMemo(() => bidderSeats.filter(({ name }) => name.toLowerCase().includes(bidderSearch.toLowerCase())), [bidderSeats, bidderSearch])
  // Built once per seat list: each spreadsheet memoizes on its specs, so a new array per render would rebuild them all on every edit.
  const bidParamSpecsBySeatId = useMemo(
    () =>
      new Map(
        bidderSeats.flatMap((bidderSeat) => {
          const catalogBidder = catalogBidders.find((c) => c.id === bidderSeat.bidderCode)
          return catalogBidder ? [[bidderSeat.id, bidParamSpecsOfSeat(bidderSeat, catalogBidder, bidderSeat.platform)] as const] : []
        })
      ),
    [bidderSeats, catalogBidders]
  )
  const hasDraft = Object.values(draftMappings).some((m) => m.length > 0)
  useConfirmNavigation(hasDraft)

  // API Hooks
  // A One seat's rows and a Node seat's go through their own product's API, so one Save makes up to two calls.
  const { mutateAsync: upsertAdmMappings, isPending: isAdmUpsertionPending } = useBulkUpsertBidParamsMapping('adm')
  const { mutateAsync: upsertNodeMappings, isPending: isNodeUpsertionPending } = useBulkUpsertBidParamsMapping('pbs')
  const isMappingsUpsertionPending = isAdmUpsertionPending || isNodeUpsertionPending

  const handleDraftChange = useCallback((bidderSeatId: string, newMappings: any) => {
    setDraftMappings((prev) => {
      // To avoid unnecessary state updates and re-renders, we check if the new mappings are the same as the current ones for the bidder seat. If they are, we return the previous state without changes.
      if (prev[bidderSeatId] === newMappings) return prev
      return { ...prev, [bidderSeatId]: newMappings }
    })
  }, [])

  const handleErroredChange = useCallback((hasError: boolean) => {
    setHasErroredMappings(hasError)
  }, [])

  const handleSave = async () => {
    const productBySeatId = new Map(bidderSeats.map((seat) => [seat.id, productOfBidderSeatType(seat.type)]))
    const [admRows, nodeRows] = partition(Object.values(draftMappings).flat(), (mapping) => productBySeatId.get(mapping.bidderSeatId) === 'adm')
    const results = await Promise.allSettled([...(admRows.length ? [upsertAdmMappings(admRows)] : []), ...(nodeRows.length ? [upsertNodeMappings(nodeRows)] : [])])
    // Whatever was written is read back, so a row saved by one call is not created again by the next Save.
    if (results.some((result) => result.status === 'fulfilled')) await refetchSavedMappings()
    if (results.some((result) => result.status === 'rejected')) {
      toast.alert('Error while saving the mapping')
      return
    }
    toast.success(
      <>
        Mapping saved for <strong>{adUnit.name}</strong>
      </>
    )
  }

  const canSave = canEdit && hasDraft && !hasErroredMappings && !isMappingsUpsertionPending
  const saveButton = (trackingLabel: TrackingLabel) => (
    <Tooltip title={disabledReason} disabled={canEdit}>
      <Button disabled={!canSave} iconName={isMappingsUpsertionPending ? 'pubstack' : undefined} iconSpin={isMappingsUpsertionPending} onClick={handleSave} trackingLabel={trackingLabel}>
        {isMappingsUpsertionPending ? 'Saving mapping...' : 'Save mapping'}
      </Button>
    </Tooltip>
  )

  return (
    <>
      <div>
        <h2>Mapping</h2>
        <span>Fill wanted lines then Save mapping. Do not remove any line or column.</span>
      </div>
      <div>
        <div className="my-3 flex w-full justify-between">
          <Input className="w-[286px]" name={'bidderSearch'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search bidder name or alias'} control={control} />
          {saveButton('adm-sites-stacks-mapping-from-ad-units-view-top-save')}
        </div>
        {filteredSeats.map((bidderSeat) => {
          const bidParamSpecs = bidParamSpecsBySeatId.get(bidderSeat.id)
          const mappings = adUnit.mappings[bidderSeat.id]
          return (
            <div key={`${adUnit.name}-${bidderSeat.name}`}>
              {bidParamSpecs && (
                <MappingsSpreadsheet
                  bidderSeat={bidderSeat}
                  mappings={mappings.bidParamsMappings}
                  bidParamSpecs={bidParamSpecs}
                  readOnly={!canEdit || (isMarketplaceAlias(bidderSeat.name) && !isAdmin)}
                  mappingDimensionsConfig={mappings.mappingDimensionsConfig}
                  showBidderInSpreadsheet={true}
                  onDraftMappingsChange={(newMappings) => handleDraftChange(bidderSeat.id, newMappings)}
                  onErroredMappingsChange={handleErroredChange}
                />
              )}
            </div>
          )
        })}
        <div className="mt-6 flex justify-end gap-5">{saveButton('adm-sites-stacks-mapping-from-ad-units-view-bottom-save')}</div>
      </div>
    </>
  )
}
